import React, { useState, ReactNode, CSSProperties } from 'react';
import Icon from '../images/Icon';

export interface Item {
  id: string;
  component: ReactNode;
  isThumbnail?: boolean;
  hasContent?: boolean;
  isImage?: boolean;
}

interface ItemCardProps {
  item: Item;
  onRemove: () => void;
  onView?: () => void;
  onUpload?: () => void;
  onLibrary?: () => void;
  onThumbnail?: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  isDragging: boolean;
  width: string | number;
  height: string | number;
  showRemove?: boolean;
  showUpload?: boolean;
  showLibrary?: boolean;
  showThumbnail?: boolean;
}

const ItemCard = ({ 
  item,
  onRemove,
  onView,
  onUpload,
  onLibrary,
  onThumbnail,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  isDragging,
  width,
  height,
  showRemove = true,
  showUpload = false,
  showLibrary = false,
  showThumbnail = false
}: ItemCardProps) => {

  const cardStyle: CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  return (
    <div 
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      style={cardStyle}
      className={`relative group bg-[#f3efff] rounded-lg border-2 transition-all cursor-move overflow-hidden ${
        isDragging 
          ? 'opacity-50 border-blue-500 scale-95' 
          : 'border-gray-200 hover:border-blue-400'
      }`}
    >
      <div className="absolute top-2 left-2 right-2 flex justify-between items-center opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <div className="flex gap-2">
          {showRemove && (
            <button
              onClick={onRemove}
              className="p-1 bg-white rounded shadow hover:bg-red-50 transition-colors"
              title="Supprimer"
            >
              <Icon name='trashCan' width={20} height={20} />
            </button>
          )}
        </div>
        
        <div className="flex gap-2">
          {showThumbnail && onThumbnail && item.hasContent && item.isImage && (
            <button
              onClick={onThumbnail}
              disabled={item.isThumbnail}
              className="p-1 bg-white rounded shadow transition-colors"
              style={{
                cursor: item.isThumbnail ? 'default' : 'pointer',
                opacity: item.isThumbnail ? 1 : undefined,
              }}
              title={item.isThumbnail ? 'Image de couverture' : 'Définir comme image de couverture'}
            >
              <Icon 
                name='star2' 
                width={20} 
                height={20} 
                color={item.isThumbnail ? '#6632FF' : '#F3EFFF'} 
              />
            </button>
          )}
          
          {showUpload && onUpload && (
            <button
              onClick={onUpload}
              className="p-1 bg-white rounded shadow hover:bg-purple-50 transition-colors"
              title="Uploader un fichier"
            >
              <Icon name='upload' width={20} height={20} color="#4421AA" />
            </button>
          )}
          
          {showLibrary && onLibrary && (
            <button
              onClick={onLibrary}
              className="p-1 bg-white rounded shadow hover:bg-purple-50 transition-colors"
              title="Choisir depuis la bibliothèque"
            >
              <Icon name='library3' width={20} height={20} color="#4421AA" />
            </button>
          )}
        </div>
      </div>
      
      <div className="w-full h-full flex items-center justify-center overflow-y-auto pt-[40px]">
        {item.component}
      </div>
    </div>
  );
};

interface CardManagerProps {
  cardWidth?: string | number;
  cardHeight?: string | number;
  containerWidth?: string | number;
  containerHeight?: string | number;
  gap?: string | number;
  columns?: number;
  showRemove?: boolean;
  showUpload?: boolean;
  showLibrary?: boolean;
  showThumbnail?: boolean;
  onAdd?: () => void;
  onRemove?: (key: string) => void;
  onView?: (id: string, item: Item) => void;
  onUpload?: (id: string) => void;
  onLibrary?: (id: string) => void;
  onThumbnail?: (id: string) => void;
  onReorder?: (reorderedIds: string[]) => void;
  items: Item[];
  fileInputRefs?: Map<string, React.RefObject<HTMLInputElement>>;
}

export default function CardManager({ 
  cardWidth = 200,
  cardHeight = 220,
  containerWidth = '100%',
  containerHeight = '500px',
  gap = 16,
  columns,
  showRemove = true,
  showUpload = false,
  showLibrary = false,
  showThumbnail = false,
  onAdd,
  onRemove,
  onView,
  onUpload,
  onLibrary,
  onThumbnail,
  onReorder,
  items,
  fileInputRefs
}: CardManagerProps) {

  const [draggedId, setDraggedId] = useState<string | null>(null);

  const containerStyle: CSSProperties = {
    width: typeof containerWidth === 'number' ? `${containerWidth}px` : containerWidth,
    height: typeof containerHeight === 'number' ? `${containerHeight}px` : containerHeight,
  };

  const gapValue = typeof gap === 'number' ? `${gap}px` : gap;

  const gridStyle: CSSProperties = columns 
    ? {
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, ${typeof cardWidth === 'number' ? `${cardWidth}px` : cardWidth})`,
        gap: gapValue,
        justifyContent: 'center',
      } 
    : {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'start',
        alignContent: 'start',
        gap: gapValue,
      };

  const getFileInputRef = (itemId: string): React.RefObject<HTMLInputElement> | null => {
    if (fileInputRefs) {
      return fileInputRefs.get(itemId) || null;
    }
    return null;
  };

  const handleAddItem = () => {
    if (onAdd) {
      onAdd();
    }
  };

  const handleViewItem = (id: string, item: Item) => {
    if (onView) {
      onView(id, item);
    }
  };

  const handleRemoveItem = (id: string) => {
    if (onRemove) {
      onRemove(id);
    }
  };

  const handleUploadItem = (id: string) => {
    console.log('🎯 CardManager handleUploadItem pour id:', id);
    
    if (onUpload) {
      onUpload(id);
    } else {
      // Comportement par défaut : essayer de trouver et cliquer sur la ref
      const fileInputRef = getFileInputRef(id);
      console.log('🎯 fileInputRef récupérée:', fileInputRef);
      console.log('🎯 fileInputRef?.current:', fileInputRef?.current);
      
      if (fileInputRef?.current) {
        console.log('✅ Click sur fileInputRef.current');
        fileInputRef.current.click();
      } else {
        console.error('❌ Aucune ref trouvée pour id:', id);
      }
    }
  };

  const handleLibraryItem = (id: string) => {
    if (onLibrary) {
      onLibrary(id);
    }
  };

  const handleThumbnailItem = (id: string) => {
    if (onThumbnail) {
      onThumbnail(id);
    }
  };

  const handleDragStart = (id: string) => (e: React.DragEvent) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedId(null);
  };

  const handleDragOver = (id: string) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (targetId: string) => (e: React.DragEvent) => {
    e.preventDefault();
    
    if (!draggedId || draggedId === targetId) return;

    const draggedIndex = items.findIndex(item => item.id === draggedId);
    const targetIndex = items.findIndex(item => item.id === targetId);

    const newItems = [...items];
    const [removed] = newItems.splice(draggedIndex, 1);
    newItems.splice(targetIndex, 0, removed);

    // Notifier le parent du réordonnancement
    if (onReorder) {
      const reorderedIds = newItems.map(item => item.id);
      onReorder(reorderedIds);
    }
  };

  return (
    <div>
      <div className="mx-auto" style={{ maxWidth: containerWidth }}>
        <div 
          className="bg-white rounded-xl shadow-lg p-4 border-2 border-gray-200 overflow-auto"
          style={containerStyle}
        >
          <div style={gridStyle}>
            {items.map((item) => (
              <ItemCard 
                key={item.id}
                item={item}
                onRemove={() => handleRemoveItem(item.id)}
                onView={() => handleViewItem(item.id, item)}
                onUpload={showUpload ? () => handleUploadItem(item.id) : undefined}
                onLibrary={showLibrary ? () => handleLibraryItem(item.id) : undefined}
                onThumbnail={showThumbnail ? () => handleThumbnailItem(item.id) : undefined}
                onDragStart={handleDragStart(item.id)}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver(item.id)}
                onDrop={handleDrop(item.id)}
                isDragging={draggedId === item.id}
                width={cardWidth}
                height={cardHeight}
                showRemove={showRemove}
                showUpload={showUpload}
                showLibrary={showLibrary}
                showThumbnail={showThumbnail}
              />
            ))}
            
            <button
              onClick={handleAddItem}
              className="bg-[#6632ff] text-white rounded-lg border-2 border-dashed border-blue-300 flex flex-col items-center justify-center gap-3 hover:from-blue-600 hover:to-indigo-700 transition-all hover:scale-105 active:scale-95"
              style={{
                width: typeof cardWidth === 'number' ? `${cardWidth}px` : cardWidth,
                height: typeof cardHeight === 'number' ? `${cardHeight}px` : cardHeight,
              }}
            >
              <Icon name='plus' width={40} height={40} color="#FFFFFF"/>
              <span className="font-semibold">Ajouter</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}